import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './SegmentAnalysis.css';

const SEGMENT_OPTIONS = [
  { value: 'brand', label: 'Brand' },
  { value: 'device', label: 'Device' },
  { value: 'page', label: 'Page' },
];

const VARIANT_COLORS = ['#8B6F4E', '#6B8E6B', '#B8860B', '#708090', '#CD853F'];

function SegmentAnalysis({ experimentId, variants }) {
  const [segmentBy, setSegmentBy] = useState('brand');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSegments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/experiments/${experimentId}/segments`, {
        params: { segmentBy },
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch segments:', err);
      setError('Failed to load segment data');
    } finally {
      setLoading(false);
    }
  }, [experimentId, segmentBy]);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  const maxRate = data?.segments
    ? Math.max(...data.segments.flatMap((seg) =>
        (seg.variants || []).map((v) => v.conversion_rate || 0)
      ), 0.001)
    : 0.001;

  if (loading) {
    return (
      <div className="segment-analysis">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading segments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="segment-analysis">
        <div className="exp-error">{error}</div>
      </div>
    );
  }

  const segments = data?.segments || [];

  return (
    <div className="segment-analysis">
      <div className="seg-header">
        <div className="seg-legend">
          {variants.map((v, i) => (
            <div key={v.id || i} className="seg-legend-item">
              <div
                className="seg-legend-color"
                style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
              />
              <span>{v.name || v.key}</span>
            </div>
          ))}
        </div>
        <div className="seg-controls">
          <label className="seg-select-label">Segment by:</label>
          <select
            className="exp-filter-select"
            value={segmentBy}
            onChange={(e) => setSegmentBy(e.target.value)}
          >
            {SEGMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {segments.length === 0 ? (
        <div className="exp-empty">
          <p>No segment data available yet.</p>
        </div>
      ) : (
        <table className="exp-table seg-table">
          <thead>
            <tr>
              <th>Segment</th>
              {variants.map((v, i) => (
                <th key={v.id || i}>
                  <div className="seg-th-variant">
                    <div
                      className="seg-th-color"
                      style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
                    />
                    {v.name || v.key}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {segments.map((seg) => (
              <tr key={seg.segment_value}>
                <td className="seg-name">{seg.segment_value || '(unknown)'}</td>
                {variants.map((v, vIdx) => {
                  const match = (seg.variants || []).find(
                    (sv) => sv.variant_id === v.id || sv.variant_key === v.key
                  );
                  const rate = match?.conversion_rate || 0;
                  const barWidth = maxRate > 0 ? (rate / maxRate) * 100 : 0;

                  return (
                    <td key={v.id || vIdx}>
                      <div className="seg-cell">
                        <span className="seg-rate">{(rate * 100).toFixed(2)}%</span>
                        <div className="seg-bar-wrapper">
                          <div
                            className="seg-bar"
                            style={{
                              width: `${barWidth}%`,
                              backgroundColor: VARIANT_COLORS[vIdx % VARIANT_COLORS.length],
                            }}
                          />
                        </div>
                        <span className="seg-visitors">
                          {(match?.visitors ?? 0).toLocaleString()} visitors
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default SegmentAnalysis;
